import { useEffect, useMemo, useRef, useState } from "react";
import { useClerk } from "@clerk/clerk-react";
import type {
  AiUsageMeter,
  Case,
  CaseStatus,
  CaseVolume,
} from "../types";
import { CASE_STATUS_LABELS } from "../types";
import Tooltip from "./ui/Tooltip";

interface Props {
  cases: Case[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onOpenSettings: () => void;
  onOpenGuide: () => void;
  userEmail: string | null;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  // Desktop collapse: App owns the state + persistence and hides the rail via
  // a class; the header chevron just calls back to toggle it.
  onToggleCollapse?: () => void;
  // Volume tree for the currently-active case. Single-volume cases
  // (length <= 1) render exactly like pre-volumes — no tree, no
  // caret, just the case row. Only shown when the active case has
  // multiple volumes.
  activeVolumes?: CaseVolume[];
  // Row click on a volume name. Lightweight action — surface the
  // Records tab, nothing else. Deliberately distinct from the kebab
  // "View summary" handler (see onViewVolumeSummary) so the two
  // intents don't collide on the same code path.
  onSelectVolume?: (volumeId: string) => void;
  // Kebab "View summary" action — switches to Records and opens that
  // volume's summary expander. Separate from onSelectVolume so the
  // summary-open side effect only fires when the user explicitly
  // asks for it.
  onViewVolumeSummary?: (volumeId: string) => void;
  // Other kebab actions. One prop per action keeps the menu's click
  // handlers explicit and grep-able.
  onAskAiAboutVolume?: (volumeId: string) => void;
  onCloseCurrentVolume?: () => void;
  onRenameVolume?: (volumeId: string, title: string) => void;
  // Group management — parent owns the network round-trips (PATCH
  // /api/cases/:id) so the cases[] prop stays in sync after the
  // mutation. Both callbacks are fire-and-forget from here.
  onAssignCaseToGroup?: (
    caseId: string,
    groupName: string | null,
  ) => void | Promise<void>;
  onDeleteGroup?: (groupName: string) => void | Promise<void>;
  // Invoked by the folder row's hover "+" icon — opens the New Case
  // form with the folder pre-selected and locked. Separate from
  // `onNew` (the sidebar-top button) so the parent can distinguish
  // "user chose a folder context" from "user clicked plain New case".
  onNewCaseInFolder?: (folderName: string) => void;
  // Compact AI usage indicator rendered above the Settings button.
  // Null while the first fetch is in flight (no meter rendered).
  aiMeter?: AiUsageMeter | null;
  // Click the meter → open Settings scrolled to the Subscription
  // section. Distinct from onOpenSettings (which opens at the top)
  // so the two entry points can be wired independently.
  onOpenSubscription?: () => void;
}

// Folders are collapsed by DEFAULT — we persist the names of the
// groups the user has explicitly EXPANDED, so a fresh session (or a
// folder we've never seen) starts closed while a refresh still
// remembers what the user opened. (Inverted from the old
// "persist the collapsed ones" model — hence the new key; any value
// under the legacy key is ignored, which simply means everything
// starts closed, the desired default.)
const EXPANDED_KEY = "casethread:sidebar-group-expanded";
// Placeholder groups created via "+ New group" before any case has
// been assigned. Groups are implicit in the backend (a group exists
// only because some case has that group_name), so we persist names
// here until the user assigns at least one case to them.
const PLACEHOLDER_KEY = "casethread:sidebar-placeholder-groups";

function loadExpanded(): Set<string> {
  try {
    const raw = window.localStorage.getItem(EXPANDED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.filter((s) => typeof s === "string")) : new Set();
  } catch {
    return new Set();
  }
}

function saveExpanded(expanded: Set<string>): void {
  try {
    window.localStorage.setItem(
      EXPANDED_KEY,
      JSON.stringify(Array.from(expanded)),
    );
  } catch {
    /* ignore */
  }
}

// Placeholder groups persist as [{ name, createdAt }] so we can
// TTL-expire them after 24h. Older entries (or the legacy string[]
// shape from before this change) are discarded on load so abandoned
// groups don't accumulate forever.
const PLACEHOLDER_TTL_MS = 24 * 60 * 60 * 1000;

interface StoredPlaceholder {
  name: string;
  createdAt: number;
}

function loadPlaceholders(now = Date.now()): string[] {
  try {
    const raw = window.localStorage.getItem(PLACEHOLDER_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const fresh: StoredPlaceholder[] = [];
    for (const entry of arr) {
      if (
        entry &&
        typeof entry === "object" &&
        typeof entry.name === "string" &&
        typeof entry.createdAt === "number" &&
        now - entry.createdAt < PLACEHOLDER_TTL_MS
      ) {
        fresh.push({ name: entry.name, createdAt: entry.createdAt });
      }
    }
    // If we pruned anything (including dropping the legacy string[]
    // shape entirely) rewrite storage so the cleanup is durable.
    if (fresh.length !== arr.length) {
      window.localStorage.setItem(PLACEHOLDER_KEY, JSON.stringify(fresh));
    }
    return fresh.map((e) => e.name);
  } catch {
    return [];
  }
}

function savePlaceholders(names: string[], now = Date.now()): void {
  try {
    // Preserve the original createdAt for names we already stored so
    // editing the list doesn't reset the TTL clock.
    let existing: StoredPlaceholder[] = [];
    const raw = window.localStorage.getItem(PLACEHOLDER_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        existing = arr.filter(
          (e): e is StoredPlaceholder =>
            e &&
            typeof e === "object" &&
            typeof e.name === "string" &&
            typeof e.createdAt === "number",
        );
      }
    }
    const byName = new Map(existing.map((e) => [e.name, e.createdAt]));
    const next: StoredPlaceholder[] = names.map((name) => ({
      name,
      createdAt: byName.get(name) ?? now,
    }));
    window.localStorage.setItem(PLACEHOLDER_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function StatusDot({ status }: { status: CaseStatus }) {
  return (
    <span
      className={`sidebar-status-dot status-${status}`}
      title={CASE_STATUS_LABELS[status]}
      aria-label={CASE_STATUS_LABELS[status]}
    />
  );
}

// Custom MIME so dataTransfer payloads can't be confused with generic
// text drops (e.g. the user dragging text from the address bar over
// the sidebar). Both the set and get sides use this exact string.
const DRAG_MIME = "application/x-casethread-case";

function CaseButton({
  caseItem,
  active,
  onSelect,
  draggable = false,
  onDragStart,
  onDragEnd,
}: {
  caseItem: Case;
  active: boolean;
  onSelect: (id: string) => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLButtonElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      className={`case-list-item${active ? " active" : ""}`}
      onClick={() => onSelect(caseItem.id)}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="case-list-name">
        <StatusDot status={caseItem.status} />
        <span>{caseItem.name}</span>
      </div>
      {caseItem.description && (
        <div className="case-list-desc">{caseItem.description}</div>
      )}
    </button>
  );
}

// CaseRow wraps CaseButton + (for the active case with >1 volume)
// renders a collapsible tree of volume children underneath. Volumes
// get their own click row + a kebab menu with per-volume actions
// (rename / view summary / ask AI / close current). Single-volume
// cases render identically to pre-volumes — CaseRow just proxies
// CaseButton, no tree, no caret.
function CaseRow({
  caseItem,
  active,
  onSelect,
  activeVolumes,
  onSelectVolume,
  onViewVolumeSummary,
  onAskAiAboutVolume,
  onCloseCurrentVolume,
  onRenameVolume,
  draggable = false,
  onDragStart,
  onDragEnd,
}: {
  caseItem: Case;
  active: boolean;
  onSelect: (id: string) => void;
  activeVolumes: CaseVolume[];
  onSelectVolume?: (volumeId: string) => void;
  onViewVolumeSummary?: (volumeId: string) => void;
  onAskAiAboutVolume?: (volumeId: string) => void;
  onCloseCurrentVolume?: () => void;
  onRenameVolume?: (volumeId: string, title: string) => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLButtonElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLButtonElement>) => void;
}) {
  // Volume tree only shows for the ACTIVE case (other cases'
  // volumes would add visual noise without a way to navigate to
  // them). Gate also on >1 volumes so single-volume cases are
  // visually unchanged.
  const showVolumes = active && activeVolumes.length > 1;
  return (
    <div className="sidebar-case-row">
      <CaseButton
        caseItem={caseItem}
        active={active}
        onSelect={onSelect}
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      />
      {showVolumes && (
        <ul className="sidebar-volume-tree">
          {[...activeVolumes]
            .sort((a, b) => a.volume_number - b.volume_number)
            .map((v) => (
              <SidebarVolumeRow
                key={v.id}
                volume={v}
                onSelect={onSelectVolume}
                onViewSummary={onViewVolumeSummary}
                onAskAi={onAskAiAboutVolume}
                onCloseCurrent={onCloseCurrentVolume}
                onRename={onRenameVolume}
              />
            ))}
        </ul>
      )}
    </div>
  );
}

function SidebarVolumeRow({
  volume,
  onSelect,
  onViewSummary,
  onAskAi,
  onCloseCurrent,
  onRename,
}: {
  volume: CaseVolume;
  onSelect?: (volumeId: string) => void;
  onViewSummary?: (volumeId: string) => void;
  onAskAi?: (volumeId: string) => void;
  onCloseCurrent?: () => void;
  onRename?: (volumeId: string, title: string) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Dismiss the kebab popover on click-outside. Tied to menuOpen so
  // the listener only exists while open — cheap and avoids stray
  // click-swallowing for the rest of the sidebar.
  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  const isOpen = volume.closed_at === null;
  const title = volume.title || (isOpen ? "Active" : `Volume ${volume.volume_number}`);

  function handleRenameClick() {
    setMenuOpen(false);
    if (!onRename) return;
    const current = volume.title ?? "";
    const next = window.prompt("Rename this volume:", current);
    if (next === null) return; // user cancelled
    onRename(volume.id, next.trim());
  }

  function handleCloseClick() {
    setMenuOpen(false);
    if (
      !window.confirm(
        "Close the current volume? A summary will be generated and a new volume will open for future records.",
      )
    ) {
      return;
    }
    onCloseCurrent?.();
  }

  return (
    <li
      className={`sidebar-volume-row${isOpen ? " open" : " closed"}`}
    >
      <button
        type="button"
        className="sidebar-volume-button"
        onClick={() => onSelect?.(volume.id)}
        title={`Volume ${volume.volume_number}${volume.title ? ` — ${volume.title}` : ""}`}
      >
        <span className="sidebar-volume-label">
          Volume {volume.volume_number} · {title}
        </span>
        <span className="sidebar-volume-count">({volume.record_count})</span>
      </button>
      <div className="sidebar-volume-menu-wrap" ref={menuRef}>
        <button
          type="button"
          className="sidebar-volume-kebab"
          aria-label="Volume actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
        >
          ⋮
        </button>
        {menuOpen && (
          <ul className="sidebar-volume-menu" role="menu">
            {onRename && (
              <li>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleRenameClick}
                >
                  Rename volume
                </button>
              </li>
            )}
            {!isOpen && onAskAi && (
              <li>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onAskAi(volume.id);
                  }}
                >
                  Ask AI about this volume
                </button>
              </li>
            )}
            {!isOpen && onViewSummary && (
              <li>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onViewSummary(volume.id);
                  }}
                >
                  View summary
                </button>
              </li>
            )}
            {isOpen && onCloseCurrent && (
              <li>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleCloseClick}
                  disabled={volume.record_count === 0}
                  title={
                    volume.record_count === 0
                      ? "Add records before closing the volume"
                      : undefined
                  }
                >
                  Close this volume
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </li>
  );
}

export default function Sidebar({
  cases,
  activeId,
  onSelect,
  onNew,
  onOpenSettings,
  onOpenGuide,
  userEmail,
  mobileOpen = false,
  onCloseMobile,
  onToggleCollapse,
  activeVolumes = [],
  onSelectVolume,
  onViewVolumeSummary,
  onAskAiAboutVolume,
  onCloseCurrentVolume,
  onRenameVolume,
  onAssignCaseToGroup,
  onDeleteGroup,
  onNewCaseInFolder,
  aiMeter,
  onOpenSubscription,
}: Props) {
  const { signOut } = useClerk();
  const [expanded, setExpandedState] = useState<Set<string>>(() =>
    loadExpanded(),
  );
  const [placeholderGroups, setPlaceholderGroups] = useState<string[]>(() =>
    loadPlaceholders(),
  );
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const newGroupInputRef = useRef<HTMLInputElement | null>(null);
  // Drop-target state: the group name currently being hovered by a
  // dragged case (or "__ungrouped__" for the ungrouped zone, or null
  // when nothing is being dragged over a valid target). Drives the
  // .drop-target class for visual feedback.
  const UNGROUPED_SENTINEL = "__ungrouped__";
  const [dragTarget, setDragTarget] = useState<string | null>(null);
  const [draggingCaseId, setDraggingCaseId] = useState<string | null>(null);

  function toggleGroup(name: string) {
    setExpandedState((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      saveExpanded(next);
      return next;
    });
  }

  useEffect(() => {
    if (newGroupOpen) newGroupInputRef.current?.focus();
  }, [newGroupOpen]);

  function commitNewGroup() {
    const name = newGroupName.trim();
    if (!name) {
      setNewGroupOpen(false);
      setNewGroupName("");
      return;
    }
    setPlaceholderGroups((prev) => {
      if (prev.some((n) => n.toLowerCase() === name.toLowerCase())) return prev;
      const next = [...prev, name];
      savePlaceholders(next);
      return next;
    });
    setNewGroupName("");
    setNewGroupOpen(false);
  }

  function cancelNewGroup() {
    setNewGroupName("");
    setNewGroupOpen(false);
  }

  // ---- Drag and drop ----
  // Reused across every draggable case row. Writes the case id into
  // dataTransfer with a custom MIME so a stray text drop on the
  // sidebar can't trigger a case move.
  function handleCaseDragStart(caseId: string) {
    return (e: React.DragEvent<HTMLButtonElement>) => {
      e.dataTransfer.setData(DRAG_MIME, caseId);
      // text/plain fallback: some browsers require at least one
      // standard MIME to be set before they'll fire drag events.
      e.dataTransfer.setData("text/plain", caseId);
      e.dataTransfer.effectAllowed = "move";
      setDraggingCaseId(caseId);
    };
  }
  function handleCaseDragEnd() {
    setDraggingCaseId(null);
    setDragTarget(null);
  }

  // `target` is a group name, or the UNGROUPED_SENTINEL. Returns
  // drag-event props to spread onto the drop-zone element.
  function dropZoneProps(target: string) {
    const isActive = dragTarget === target;
    const accepts = () =>
      draggingCaseId !== null || onAssignCaseToGroup !== undefined;
    return {
      onDragOver: (e: React.DragEvent<HTMLElement>) => {
        if (!accepts()) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dragTarget !== target) setDragTarget(target);
      },
      onDragLeave: (e: React.DragEvent<HTMLElement>) => {
        // Only clear if the pointer actually left the element (not
        // just moved onto a child). relatedTarget is the node the
        // cursor entered; if it's still inside us, ignore.
        const next = e.relatedTarget as Node | null;
        if (
          next &&
          e.currentTarget instanceof Node &&
          e.currentTarget.contains(next)
        ) {
          return;
        }
        if (dragTarget === target) setDragTarget(null);
      },
      onDrop: (e: React.DragEvent<HTMLElement>) => {
        if (!accepts()) return;
        e.preventDefault();
        const caseId =
          e.dataTransfer.getData(DRAG_MIME) ||
          e.dataTransfer.getData("text/plain");
        setDragTarget(null);
        setDraggingCaseId(null);
        if (!caseId) return;
        const nextGroup = target === UNGROUPED_SENTINEL ? null : target;
        void onAssignCaseToGroup?.(caseId, nextGroup);
      },
      className: isActive ? "drop-target" : undefined,
    };
  }

  function handleDeleteGroup(name: string) {
    const membersInThisGroup = cases.filter(
      (c) => (c.group_name ?? null) === name,
    ).length;
    const msg =
      membersInThisGroup > 0
        ? `Delete folder "${name}"? ${membersInThisGroup} case${
            membersInThisGroup === 1 ? "" : "s"
          } in this folder will be moved out, not deleted.`
        : `Delete folder "${name}"?`;
    if (!window.confirm(msg)) return;
    // Drop any placeholder entry for the group — if this group was
    // placeholder-only (no real cases), that's enough to make it
    // disappear. Then fire the parent callback so cases with this
    // group_name get patched to null on the server.
    setPlaceholderGroups((prev) => {
      const next = prev.filter((n) => n !== name);
      if (next.length !== prev.length) savePlaceholders(next);
      return next;
    });
    if (membersInThisGroup > 0) void onDeleteGroup?.(name);
  }

  // Group cases: named groups first (alphabetical), ungrouped last.
  // Within each group cases are sorted by the most recently-active
  // signal we have to hand — summary_updated_at, falling back to
  // created_at. Good enough for a sidebar; nothing here is load-bearing.
  const { groupedCases, ungroupedCases, hasAnyGroup } = useMemo(() => {
    const byGroup = new Map<string, Case[]>();
    const ungrouped: Case[] = [];
    for (const c of cases) {
      const g = c.group_name?.trim();
      if (g) {
        const arr = byGroup.get(g) ?? [];
        arr.push(c);
        byGroup.set(g, arr);
      } else {
        ungrouped.push(c);
      }
    }
    const recency = (c: Case) => c.summary_updated_at ?? c.created_at ?? "";
    const byRecent = (a: Case, b: Case) => recency(b).localeCompare(recency(a));
    for (const [, arr] of byGroup) arr.sort(byRecent);
    ungrouped.sort(byRecent);
    // Merge in placeholder groups the user created via "+ New group"
    // but haven't assigned any cases to yet. Skip any that have since
    // become real (avoids duplicates if a case was added via the
    // NewCase form).
    for (const name of placeholderGroups) {
      if (!byGroup.has(name)) byGroup.set(name, []);
    }
    const groupedSorted = Array.from(byGroup.entries()).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
    return {
      groupedCases: groupedSorted,
      ungroupedCases: ungrouped,
      hasAnyGroup: groupedSorted.length > 0,
    };
  }, [cases, placeholderGroups]);

  // Once a placeholder group has a real case in it, drop it from the
  // localStorage list — the group is now "real" and will persist
  // naturally through the case's group_name.
  useEffect(() => {
    if (placeholderGroups.length === 0) return;
    const realNames = new Set(
      cases.map((c) => c.group_name?.trim()).filter((n): n is string => !!n),
    );
    const stillPlaceholder = placeholderGroups.filter(
      (n) => !realNames.has(n),
    );
    if (stillPlaceholder.length !== placeholderGroups.length) {
      savePlaceholders(stillPlaceholder);
      setPlaceholderGroups(stillPlaceholder);
    }
  }, [cases, placeholderGroups]);

  // Auto-clear expanded state for groups that no longer exist so the
  // localStorage set doesn't grow forever.
  useEffect(() => {
    if (expanded.size === 0) return;
    const liveNames = new Set(groupedCases.map(([name]) => name));
    let changed = false;
    const next = new Set(expanded);
    for (const name of next) {
      if (!liveNames.has(name)) {
        next.delete(name);
        changed = true;
      }
    }
    if (changed) {
      saveExpanded(next);
      setExpandedState(next);
    }
  }, [groupedCases, expanded]);

  return (
    <aside
      className={`sidebar${mobileOpen ? " mobile-open" : ""}`}
      aria-hidden={
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 767px)").matches
          ? !mobileOpen
          : undefined
      }
    >
      <div className="sidebar-header">
        <h2 className="sidebar-brand">
          <img
            src="/casethread-icon.png?v=2"
            alt=""
            className="sidebar-brand-icon"
            width={28}
            height={28}
          />
          <span>CaseThread</span>
        </h2>
        {userEmail && <div className="sidebar-user">{userEmail}</div>}
        {onToggleCollapse && (
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label="Collapse navigation"
            title="Collapse navigation"
          >
            <span aria-hidden>«</span>
          </button>
        )}
        {onCloseMobile && (
          <button
            type="button"
            className="sidebar-close-mobile"
            onClick={onCloseMobile}
            aria-label="Close navigation"
          >
            ✕
          </button>
        )}
      </div>
      <Tooltip title="Create a new case">
        <button className="btn-primary btn-new-case" onClick={onNew}>
          + New case
        </button>
      </Tooltip>
      <nav className="case-list">
        {cases.length === 0 && (
          <div className="case-list-empty">No cases yet.</div>
        )}

        {/* Ungrouped bucket — rendered FIRST when any folder exists,
            so the user's loose cases are the first thing they see
            when they open the sidebar. Collapses to nothing when
            every case has been filed into a folder. Kept visible
            during a drag so the user has a drop target for
            removing a case from its folder. */}
        {hasAnyGroup &&
          (ungroupedCases.length > 0 || draggingCaseId !== null) &&
          (() => {
            const dropProps = dropZoneProps(UNGROUPED_SENTINEL);
            return (
              <div
                className={`sidebar-group sidebar-group-ungrouped${
                  dropProps.className ? " " + dropProps.className : ""
                }`}
                onDragOver={dropProps.onDragOver}
                onDragLeave={dropProps.onDragLeave}
                onDrop={dropProps.onDrop}
              >
                <div className="sidebar-group-head static">
                  <span className="sidebar-group-name muted">Ungrouped</span>
                  <span className="sidebar-group-count">
                    ({ungroupedCases.length})
                  </span>
                </div>
                <div className="sidebar-group-body">
                  {ungroupedCases.length === 0 ? (
                    <div className="sidebar-group-empty">
                      Drop here to remove from folder.
                    </div>
                  ) : (
                    ungroupedCases.map((c) => (
                      <CaseRow
                        key={c.id}
                        caseItem={c}
                        active={c.id === activeId}
                        onSelect={onSelect}
                        activeVolumes={activeVolumes}
                        onSelectVolume={onSelectVolume}
                        onViewVolumeSummary={onViewVolumeSummary}
                        onAskAiAboutVolume={onAskAiAboutVolume}
                        onCloseCurrentVolume={onCloseCurrentVolume}
                        onRenameVolume={onRenameVolume}
                        draggable={!!onAssignCaseToGroup}
                        onDragStart={handleCaseDragStart(c.id)}
                        onDragEnd={handleCaseDragEnd}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })()}

        {/* Named folders — the primary organizational unit. */}
        {groupedCases.map(([name, members]) => {
          const isCollapsed = !expanded.has(name);
          const dropProps = dropZoneProps(name);
          return (
            <div
              key={name}
              className={`sidebar-group sidebar-folder${
                dropProps.className ? " " + dropProps.className : ""
              }`}
              onDragOver={dropProps.onDragOver}
              onDragLeave={dropProps.onDragLeave}
              onDrop={dropProps.onDrop}
            >
              <div className="sidebar-group-head-row">
                <button
                  type="button"
                  className={`sidebar-group-head sidebar-folder-head${isCollapsed ? " collapsed" : ""}`}
                  onClick={() => toggleGroup(name)}
                  aria-expanded={!isCollapsed}
                >
                  <span className="sidebar-group-caret" aria-hidden>
                    {isCollapsed ? "▸" : "▾"}
                  </span>
                  <span className="sidebar-folder-icon" aria-hidden>
                    📁
                  </span>
                  <span className="sidebar-group-name sidebar-folder-name">
                    {name}
                  </span>
                  <span className="sidebar-group-count">
                    ({members.length})
                  </span>
                </button>
                {onNewCaseInFolder && (
                  <Tooltip title={`New case in ${name}`}>
                    <button
                      type="button"
                      className="sidebar-folder-add"
                      onClick={() => onNewCaseInFolder(name)}
                      aria-label={`New case in ${name}`}
                    >
                      +
                    </button>
                  </Tooltip>
                )}
                <Tooltip title="Delete this folder (cases are kept, just moved out of the folder)">
                  <button
                    type="button"
                    className="sidebar-group-delete"
                    onClick={() => handleDeleteGroup(name)}
                    aria-label={`Delete folder ${name}`}
                  >
                    ✕
                  </button>
                </Tooltip>
              </div>
              {!isCollapsed && (
                <div className="sidebar-group-body">
                  {members.length === 0 ? (
                    <div className="sidebar-group-empty">
                      Drag a case here to add it to this folder.
                    </div>
                  ) : (
                    members.map((c) => (
                      <CaseRow
                        key={c.id}
                        caseItem={c}
                        active={c.id === activeId}
                        onSelect={onSelect}
                        activeVolumes={activeVolumes}
                        onSelectVolume={onSelectVolume}
                        onViewVolumeSummary={onViewVolumeSummary}
                        onAskAiAboutVolume={onAskAiAboutVolume}
                        onCloseCurrentVolume={onCloseCurrentVolume}
                        onRenameVolume={onRenameVolume}
                        draggable={!!onAssignCaseToGroup}
                        onDragStart={handleCaseDragStart(c.id)}
                        onDragEnd={handleCaseDragEnd}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* No folders at all — render the flat list directly. */}
        {!hasAnyGroup &&
          ungroupedCases.map((c) => (
            <CaseRow
              key={c.id}
              caseItem={c}
              active={c.id === activeId}
              onSelect={onSelect}
              activeVolumes={activeVolumes}
              onSelectVolume={onSelectVolume}
              onViewVolumeSummary={onViewVolumeSummary}
              onAskAiAboutVolume={onAskAiAboutVolume}
              onCloseCurrentVolume={onCloseCurrentVolume}
              onRenameVolume={onRenameVolume}
            />
          ))}

        {/* Bottom text link — secondary action, not a prominent
            button. Renders as a muted text link rather than a
            dashed box so the "+ New case" button at the top stays
            the primary call to action. */}
        {newGroupOpen ? (
          <div className="sidebar-new-group-inline">
            <input
              ref={newGroupInputRef}
              type="text"
              className="sidebar-new-group-input"
              placeholder="Folder name"
              value={newGroupName}
              maxLength={60}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitNewGroup();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelNewGroup();
                }
              }}
              onBlur={commitNewGroup}
            />
          </div>
        ) : (
          <button
            type="button"
            className="btn-new-folder"
            onClick={() => setNewGroupOpen(true)}
          >
            + New folder
          </button>
        )}
      </nav>
      <div className="sidebar-footer">
        {aiMeter && (
          <SidebarAiMeter
            meter={aiMeter}
            onOpenSubscription={onOpenSubscription}
          />
        )}
        <Tooltip title="App settings">
          <button
            type="button"
            className="sidebar-settings"
            onClick={onOpenSettings}
            aria-label="App settings"
          >
            <span aria-hidden>⚙</span> Settings
          </button>
        </Tooltip>
        <button
          type="button"
          className="sidebar-signout"
          onClick={() => signOut({ redirectUrl: "/welcome" })}
        >
          Sign out
        </button>
        <div className="sidebar-legal">
          <button
            type="button"
            className="sidebar-legal-link"
            onClick={onOpenGuide}
          >
            Guide
          </button>{" "}
          · <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a> ·{" "}
          <a href="/changelog">Changelog</a> ·{" "}
          <a href="mailto:feedback@casethread.app">Feedback</a>
        </div>
        <div className="sidebar-copyright">
          © {new Date().getUTCFullYear()} CaseThread
        </div>
      </div>
    </aside>
  );
}

// Compact AI usage indicator. Same font size as the legal link row
// below it — intended as a status strip, not a dashboard widget.
// Colour is driven by usage ratio with fixed 60% / 85% breakpoints.
function SidebarAiMeter({
  meter,
  onOpenSubscription,
}: {
  meter: AiUsageMeter;
  onOpenSubscription?: (() => void) | undefined;
}) {
  const clickable = !!onOpenSubscription;
  const byok = meter.byo_key_active;

  // BYOK: no bar, just a text pill. Users know their own provider is
  // paying for it, so there's no limit to visualise.
  if (byok) {
    return (
      <button
        type="button"
        className="sidebar-ai-meter byok"
        onClick={onOpenSubscription}
        disabled={!clickable}
        aria-label="AI usage — using your own API key"
        title="Using your own API key"
      >
        <span className="sidebar-ai-meter-text">Unlimited (your key)</span>
      </button>
    );
  }

  // Plan limit is always present for non-BYOK users, but guard for
  // the edge case where the worker omitted it — render a neutral
  // "N calls" pill with no bar rather than dividing by null.
  if (meter.limit === null) {
    return (
      <button
        type="button"
        className="sidebar-ai-meter"
        onClick={onOpenSubscription}
        disabled={!clickable}
        aria-label={`AI usage — ${meter.ai_calls} calls this month`}
      >
        <span className="sidebar-ai-meter-text">
          {meter.ai_calls.toLocaleString()} AI calls
        </span>
      </button>
    );
  }

  const limit = meter.limit;
  const ratio = Math.min(1, meter.ai_calls / Math.max(1, limit));
  const pct = Math.round(ratio * 100);
  const level: "ok" | "warn" | "crit" =
    ratio >= 0.85 ? "crit" : ratio >= 0.6 ? "warn" : "ok";

  return (
    <button
      type="button"
      className={`sidebar-ai-meter level-${level}`}
      onClick={onOpenSubscription}
      disabled={!clickable}
      aria-label={`AI usage ${meter.ai_calls} of ${limit}`}
      title={`AI usage — ${pct}% of this month's allowance used`}
    >
      <div className="sidebar-ai-meter-bar" aria-hidden>
        <div
          className="sidebar-ai-meter-bar-fill"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="sidebar-ai-meter-text">
        {meter.ai_calls.toLocaleString()} / {limit.toLocaleString()} AI
        calls
      </span>
    </button>
  );
}
